import React, { useRef, useState, useCallback, useEffect } from "react"
import { RotateCcw, Upload, Video } from "lucide-react"
import { toast } from "sonner"
import fixWebmDuration from "fix-webm-duration"
import { Button } from "@/modules/shared/ui/components/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/modules/shared/ui/components/dialog"
import { 
  VIDEO_WIDTH, 
  VIDEO_HEIGHT, 
  VIDEO_FRAME_RATE, 
  VIDEO_BITS_PER_SECOND,
  AUDIO_BITS_PER_SECOND,
  MAX_RECORDING_SECONDS,
  WARNING_SECONDS_REMAINING,
  MAX_UPLOAD_BYTES,
  SIZE_SAFETY_MARGIN,
  RECORDER_TIMESLICE_MS,
  SECONDS_PER_MINUTE,
  MAX_RECORDING_MINUTES,
  MAX_UPLOAD_MB
} from "@/modules/public/constants/videoInterviewLimits"

const SUPPORTED_MIME_TYPES = [
  "video/webm;codecs=vp9,opus",
  "video/webm;codecs=vp8,opus",
  "video/webm",
  "video/mp4",
]

const WEBM_MIME_PREFIX = "video/webm"
const MS_PER_SECOND = 1000
const SECONDS_PAD_LENGTH = 2

function getSupportedMimeType(): string {
  return SUPPORTED_MIME_TYPES.find((type) => MediaRecorder.isTypeSupported(type)) ?? ""
}

interface VideoRecorderProps {
  onUpload: (blob: Blob) => void
  isUploading: boolean
  uploadProgress?: number
}

export default function VideoRecorder({
  onUpload,
  isUploading,
  uploadProgress = 0,
}: VideoRecorderProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const [stream, setStream] = useState<MediaStream | null>(null)
  const [recording, setRecording] = useState(false)
  const [recordedVideo, setRecordedVideo] = useState<Blob | null>(null)
  const [videoURL, setVideoURL] = useState<string | null>(null)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [sizeError, setSizeError] = useState(false)
  const totalBytesRef = useRef(0)

  const streamRef = useRef<MediaStream | null>(null)

  const startCamera = useCallback(async () => {
    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: VIDEO_WIDTH, max: VIDEO_WIDTH },
          height: { ideal: VIDEO_HEIGHT, max: VIDEO_HEIGHT },
          frameRate: { ideal: VIDEO_FRAME_RATE, max: VIDEO_FRAME_RATE },
        },
        audio: true,
      })
      setStream(mediaStream)
      streamRef.current = mediaStream
      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream
      }
    } catch (_err) {
      setCameraError(
        "Could not access your camera. Please check browser permissions and try again.",
      )
    }
  }, [])

   const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      setStream(null)
      streamRef.current = null
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null
    }
  }, [])

   const handleStopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== "inactive") {
      recorder.stop()
      setRecording(false)
      stopCamera()
    }
  }, [stopCamera])

  useEffect(() => {
    startCamera()
    return () => {
      stopCamera()
    }
  }, [startCamera, stopCamera])

  useEffect(() => {
    return () => {
      if (videoURL) {
        URL.revokeObjectURL(videoURL)
      }
    }
  }, [videoURL])

  const handleStartRecording = useCallback(() => {
    if (!stream) return

    setRecordedVideo(null)
    setVideoURL(null)
    totalBytesRef.current = 0

    const mimeType = getSupportedMimeType()
    const mediaRecorder = new MediaRecorder(stream, {
      mimeType,
      videoBitsPerSecond: VIDEO_BITS_PER_SECOND,
      audioBitsPerSecond: AUDIO_BITS_PER_SECOND,
    })
    mediaRecorderRef.current = mediaRecorder
    const chunks: Blob[] = []

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunks.push(event.data)
        totalBytesRef.current += event.data.size
        if (totalBytesRef.current >= MAX_UPLOAD_BYTES * SIZE_SAFETY_MARGIN) {
          handleStopRecording()
          toast.info(
            `Maximum file size of ${MAX_UPLOAD_MB} MB reached. Your recording has been saved.`,
          )
        }
      }
    }

    const startedAt = Date.now()

    mediaRecorder.onstop = async () => {
      const rawBlob = new Blob(chunks, { type: mediaRecorder.mimeType })
      const blob = rawBlob.type.startsWith(WEBM_MIME_PREFIX)
        ? await fixWebmDuration(rawBlob, Date.now() - startedAt)
        : rawBlob
      setRecordedVideo(blob)
      setVideoURL(URL.createObjectURL(blob))
      setSizeError(blob.size > MAX_UPLOAD_BYTES)
    }

    mediaRecorder.start(RECORDER_TIMESLICE_MS)
    setRecording(true)
  }, [stream, handleStopRecording])

  useEffect(() => {
    if (!recording) {
      setElapsedSeconds(0)
      return
    }
    const interval = setInterval(() => setElapsedSeconds((prev) => prev + 1), MS_PER_SECOND)
    return () => clearInterval(interval)
  }, [recording])

  useEffect(() => {
    if (recording && elapsedSeconds >= MAX_RECORDING_SECONDS) {
      handleStopRecording()
      toast.info(
        `Maximum recording length of ${MAX_RECORDING_MINUTES} minutes reached. Your recording has been saved.`,
      )
    }
  }, [recording, elapsedSeconds, handleStopRecording])

  const handleRetake = useCallback(() => {
    if (videoURL) {
      URL.revokeObjectURL(videoURL)
    }
    setRecordedVideo(null)
    setVideoURL(null)
    setSizeError(false)
    if (!stream) startCamera()
  }, [startCamera, stream, videoURL])

  const secondsRemaining = MAX_RECORDING_SECONDS - elapsedSeconds
  const showTimeWarning  = recording && secondsRemaining <= WARNING_SECONDS_REMAINING
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / SECONDS_PER_MINUTE)
    const secs = seconds % SECONDS_PER_MINUTE
    return `${mins}:${secs.toString().padStart(SECONDS_PAD_LENGTH, "0")} / ${MAX_RECORDING_MINUTES}:00`
  }

  return (
    <>
      <div className="flex flex-col h-full bg-card rounded-2xl overflow-hidden shadow-sm relative">
        <div className="flex-1 relative bg-muted flex items-center justify-center rounded-t-2xl overflow-hidden">
          {!stream && !recordedVideo && (
            <div className="text-muted-foreground flex flex-col items-center justify-center text-center p-6">
              <div className="w-16 h-16 bg-card rounded-full flex items-center justify-center mb-4 shadow-sm">
                <Video className="w-8 h-8 text-muted-foreground" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-1">
                Camera Access Required
              </h3>
              <p className="max-w-xs mx-auto mb-6">
                Please enable camera access to record your video response.
              </p>
              <Button
                onClick={startCamera}
                className="bg-[#1b237a] text-white rounded-xl text-sm font-semibold hover:bg-[#1a2a8f] shadow-lg shadow-blue-900/20"
              >
                Enable Camera
              </Button>
            </div>
          )}

          {/* Live Preview */}
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            className={`w-full h-full object-cover ${stream && !recordedVideo ? "block" : "hidden"}`}
          />

          {/* Recorded Playback */}
          {videoURL && (
            <video
              src={videoURL}
              controls
              className="w-full h-full object-contain"
            />
          )}

          {/* Recording Indicator & Timer */}
          {recording && (
            <div className="absolute top-6 left-6 flex items-center gap-2 bg-red-600/90 text-white px-4 py-2 rounded-full backdrop-blur-sm shadow-sm transition-all">
              <div className="w-2.5 h-2.5 rounded-full bg-white animate-pulse" />
              <span className="font-mono font-bold tracking-wider">
                {formatTime(elapsedSeconds)}
              </span>
            </div>
          )}
            {showTimeWarning && (
            <div className="absolute top-6 right-6 bg-amber-500/90 text-white px-4 py-2 rounded-full backdrop-blur-sm shadow-sm">
              Less than {Math.ceil(secondsRemaining / SECONDS_PER_MINUTE)} minute remaining
            </div>
          )}
        </div>

        {/* Controls Bar */}
        <div className="min-h-24 py-2 bg-card flex flex-col justify-center px-8 gap-2">
          {isUploading && (
            <div className="w-full">
              <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full bg-[#1b237a] transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1 text-center">
                Uploading… {uploadProgress}%. Please don&apos;t close this page.
              </p>
            </div>
          )}
          {!recordedVideo ? (
            <div className="w-full flex flex-col items-center justify-center gap-2">
              <p className="text-xs text-muted-foreground">
                Max {MAX_RECORDING_MINUTES} minutes · {VIDEO_HEIGHT}p · Max {MAX_UPLOAD_MB} MB
              </p>
              {!recording ? (
                <button
                  onClick={handleStartRecording}
                  disabled={!stream}
                  className="group flex flex-col items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="w-16 h-16 rounded-full border-[3px] border-border flex items-center justify-center transition-all hover:border-red-500/30">
                    <div className="w-14 h-14 rounded-full bg-red-600 shadow-sm transition-all group-hover:scale-90" />
                  </div>
                  <span className="text-muted-foreground text-sm font-medium group-hover:text-red-600">
                    Record
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleStopRecording}
                  className="group flex flex-col items-center gap-2"
                >
                  <div className="w-16 h-16 rounded-full border-[3px] border-border flex items-center justify-center transition-all hover:scale-105 hover:border-red-500/30">
                    <div className="w-8 h-8 rounded-md bg-red-600 shadow-sm transition-transform group-hover:scale-90" />
                  </div>
                  <span className="text-muted-foreground text-sm font-medium group-hover:text-red-600">
                    Stop
                  </span>
                </button>
              )}
            </div>
          ) : (
            <>
            {sizeError && (
              <p className="text-sm text-red-600 text-center">
                Your recording is larger than the {MAX_UPLOAD_MB} MB limit. Please re-record.
              </p>
            )}
            <div className="w-full flex items-center justify-between">
              <Button
                variant="ghost"
                onClick={handleRetake}
                disabled={isUploading}
                className="gap-2"
              >
                <RotateCcw className="w-5 h-5" />
                Retake
              </Button>

              <Button
                onClick={() => {
                  if (recordedVideo) {
                    onUpload(recordedVideo)
                  } else {
                    toast.error("No recorded video to upload")
                  }
                }}
                disabled={isUploading || sizeError}
                className="gap-2 bg-[#1b237a] hover:bg-[#1a2a8f] text-white rounded-xl font-semibold shadow-xl shadow-blue-900/20 transition-all hover:-translate-y-0.5"
              >
                {isUploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Submit Video
                  </>
                )}
              </Button>
            </div>
            </>
          )}
        </div>
      </div>

      {/* Camera Error Dialog */}
      <Dialog
        open={!!cameraError}
        onOpenChange={(open) => !open && setCameraError(null)}
      >
        <DialogContent className="sm:max-w-[425px] bg-card">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Video className="size-5 text-destructive" />
              Error accessing camera
            </DialogTitle>
            <DialogDescription className="text-muted-foreground mt-2">
              {cameraError}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4 gap-2">
            <Button variant="outline" onClick={() => setCameraError(null)}>
              Dismiss
            </Button>
            <Button
              variant="destructive"
              className="bg-red-600 hover:bg-red-700"
              onClick={() => {
                setCameraError(null)
                startCamera()
              }}
            >
              Retry camera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
